import React, { useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { isBranchDeployFn } from '@confluence/atlassian-internal-help-items';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import {
	COMPANY_HUB,
	COMPANY_HUB_EDIT,
	EMBED_VIEW,
	EDIT_PAGE,
	EDIT_PAGE_V2,
	EDIT_BLOG,
	EDIT_BLOG_V2,
	EDIT_PAGE_EMBED,
	EDIT_PAGE_SMART_CREATE,
} from '@confluence/named-routes/entry-points/routes.ts';
import {
	isViewPage,
	isBlogPage,
	isHomePage,
	isSpaceOverview,
	isDatabase,
	isWhiteboard,
} from '@confluence/named-routes/entry-points/routes-utils.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';

import { RenderFailureErrorMessage } from './RenderFailureErrorMessage.tsx';
import { RenderHydrationFailureMessage } from './RenderHydrationFailureMessage.tsx';

export const i18n: {
	ssrRenderErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	hydrationErrorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	ssrRenderErrorFlagTitle: {
		id: 'ssr-render-failure-notification.flag',
		defaultMessage: 'This page was NOT SSR RENDERED!',
		description: 'Title to be shown when the SSR render failure flag is showing.',
	},
	hydrationErrorFlagTitle: {
		id: 'ssr-render-failure-notification.hydration.flag',
		defaultMessage: 'This page was NOT HYDRATED PROPERLY!',
		description: 'Title to be shown when the hydration failure flag is showing.',
	},
});

export const SSRRenderFailureNotificationComponent: React.ComponentType<Omit<unknown, 'flags'>> =
	withFlags(({ flags }: WithFlagsProps) => {
		const hasShownErrorFlagRef = useRef<boolean>(false);
		const { environment } = useSessionData();

		// we want to exclude testing environments and pollinator tenants from showing the error flag
		const isTestEnvironment = StatsigConfigurations.isKillSwitchOn(
			'confluence_frontend_ssr_failure_notification',
		);

		// We only want to show the error flag in the dev/staging environment
		// however, branch deploys on production will default to process.env.CLOUD_ENV = 'branch'
		const isStagingEnvironment =
			['dev', 'branch', 'staging', 'stg-fedm'].includes(process.env.CLOUD_ENV ?? '') &&
			environment !== 'PRODUCTION';

		useEffect(() => {
			if (!isStagingEnvironment || isTestEnvironment) {
				return;
			}

			// regex to match /spaces/:spaceKey/blogs (blogs overview)
			const blogsOverviewRegex = new RegExp(`^${CONTEXT_PATH}/spaces/[^/]+/blogs$`);

			// Helper function to determine if we should skip error checking for certain paths
			const isAllowedPath = (pathname: string): boolean => {
				return (
					isHomePage() ||
					isViewPage() ||
					isSpaceOverview() ||
					isBlogPage() ||
					isWhiteboard() ||
					isDatabase() ||
					Boolean(EDIT_PAGE.match(pathname)) ||
					Boolean(EDIT_PAGE_V2.match(pathname)) ||
					Boolean(EDIT_BLOG.match(pathname)) ||
					Boolean(EDIT_BLOG_V2.match(pathname)) ||
					Boolean(EDIT_PAGE_EMBED.match(pathname)) ||
					Boolean(blogsOverviewRegex.test(pathname)) ||
					Boolean(COMPANY_HUB.match(pathname)) ||
					Boolean(COMPANY_HUB_EDIT.match(pathname)) ||
					Boolean(EMBED_VIEW.match(pathname)) ||
					Boolean(EDIT_PAGE_SMART_CREATE.match(pathname)) ||
					Boolean('/'.match(pathname))
				);
			};

			if (isAllowedPath(window.location.pathname)) {
				const showHydrationError = () => {
					if (!hasShownErrorFlagRef.current) {
						void flags.showErrorFlag({
							title: <FormattedMessage {...i18n.hydrationErrorFlagTitle} />,
							isAutoDismiss: false,
							description: <RenderHydrationFailureMessage />,
						});
						hasShownErrorFlagRef.current = true;
					}
				};

				if (StatsigConfigurations.getBoolean('confluence_frontend_hydration_error_event', false)) {
					window.addEventListener('hydrationError', showHydrationError as EventListener);
				}

				// sometimes errors exist but the page is still SSRd successfully - we don't want to show the SSR render error flag in this case
				if (window.__SSR_RENDERED__) {
					// if the page is SSRd successfully we check if the page was hydrated successfully, and show a hydration error flag if not
					if ((window as any).__HYDRATION_FAILED__) {
						showHydrationError();
					}
					return () => {
						window.removeEventListener('hydrationError', showHydrationError as EventListener);
					};
				}

				// COMPILED dev warnings show up on every SSRd page even if the page is SSRd successfully,
				// so we need to filter them out to distinguish from actual errors being reported for local dev ssr
				const ssrErrors = window.__SSR_ERROR__?.filter((err) => !err.includes('DEV WARNING')) ?? [];

				// on old branch deploys, ssr errors can still be empty but the page might not be SSRd successfully
				const isBranchDeployFailure =
					isBranchDeployFn() && !window.__SSR_RENDERED__ && !ssrErrors?.length;

				if (!hasShownErrorFlagRef.current && (isBranchDeployFailure || ssrErrors.length)) {
					void flags.showErrorFlag({
						title: <FormattedMessage {...i18n.ssrRenderErrorFlagTitle} />,
						isAutoDismiss: false,
						description: (
							<RenderFailureErrorMessage
								ssrErrors={ssrErrors}
								isBranchDeployFailure={isBranchDeployFailure}
							/>
						),
					});
					hasShownErrorFlagRef.current = true;
				}

				return () => {
					window.removeEventListener('hydrationError', showHydrationError as EventListener);
				};
			}
		}, [isStagingEnvironment, flags, isTestEnvironment]);

		return null;
	});

export const SSRRenderFailureNotification = (): React.JSX.Element => {
	return (
		<TransparentErrorBoundary attribution={Attribution.BACKBONE}>
			<SSRRenderFailureNotificationComponent />
		</TransparentErrorBoundary>
	);
};
